import React, { useEffect, useRef, useState } from "react";
import type {
  TeamPopupClickAction,
  TeamPopupItemPopulated,
} from "../../shared/interfaces/team";
import {
  BackButtonArea,
  LeftItem,
  MomentoModal,
  Overlay,
  MomentoModalContent,
} from "./styled";
import { BasicImgWrapper, BasicText, BasicButton, FontFamilyTypeEnum, COLORS } from "../styled";
import ExitIcon from "../../assets/images/header/ExitIcon.png";

interface IProps {
  src?: string;
  /** When set, the creative is tappable and triggers configured actions via onTapImage. */
  popup?: TeamPopupItemPopulated | null;
  onCloseModal: () => void;
  onTapImage?: () => void;
  /** Overlay tap does not dismiss; use “Not now”, primary action, or the close control. */
  requireExplicitDismiss?: boolean;
}

function popupCopy(action: TeamPopupClickAction): {
  title: string;
  body: string;
  primaryLabel: string;
} {
  switch (action) {
    case "feedback":
      return {
        title: "We’d love your feedback",
        body: "We’d love to hear what you think of the experience, and if there’s anything you think we can do to improve the platform. It’s optional—use Give feedback when you’re ready, or Not now to keep browsing.",
        primaryLabel: "Give feedback",
      };
    case "share_with_friends":
      return {
        title: "Share with friends",
        body: "Invite friends by email with a link to this game. Only if you want to.",
        primaryLabel: "Invite friends",
      };
    default:
      return { title: "", body: "", primaryLabel: "Continue" };
  }
}

const TeamPopupComponent = ({
  src,
  popup,
  onCloseModal,
  onTapImage,
  requireExplicitDismiss = false,
}: IProps): JSX.Element => {
  const mainAreaRef = useRef<HTMLDivElement>(null);

  const [canvasAreaSize, setCanvasAreaSize] = useState("100%");
  const [imageAreaSize, setImageAreaSize] = useState("100%");
  const [sizesCalculated, setSizesCalculated] = useState(false);

  const textOnly = !!popup && (popup.clickAction === "feedback" || popup.clickAction === "share_with_friends");

  useEffect(() => {
    if (textOnly) return;
    if (mainAreaRef && mainAreaRef.current) {
      const { clientWidth, clientHeight } = mainAreaRef.current;
      const min = Math.min(clientWidth, clientHeight - 120);
      const size = Math.max(min, 0);
      setCanvasAreaSize(`${size}px`);
      setImageAreaSize(`${size > 30 ? size - 30 : 1}px`);
      setSizesCalculated(true);
    }
  }, [textOnly]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (requireExplicitDismiss) return;
    if (e.target === e.currentTarget) {
      onCloseModal();
    }
  };

  const copy = popup ? popupCopy(popup.clickAction) : null;

  const footerRow = (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        justifyContent: "center",
        alignItems: "center",
        padding: "12px 16px 20px",
        width: "100%",
      }}
    >
      <BasicButton
        type="button"
        onClick={onCloseModal}
        backgroundColor={COLORS.WHITE}
        borderColor={COLORS.GREY}
        margin="0"
        fontFamily={FontFamilyTypeEnum.MONTSERRAT}
      >
        Not now
      </BasicButton>
      {textOnly && onTapImage && copy?.primaryLabel ? (
        <BasicButton
          type="button"
          onClick={onTapImage}
          backgroundColor={COLORS.BLACK_MAIN}
          borderColor={COLORS.BLACK_MAIN}
          color={COLORS.WHITE}
          margin="0"
          fontFamily={FontFamilyTypeEnum.MONTSERRAT}
        >
          {copy.primaryLabel}
        </BasicButton>
      ) : null}
    </div>
  );

  return (
    <>
      <Overlay onClick={handleOverlayClick} />
      <MomentoModal ref={mainAreaRef}>
        {textOnly && copy ? (
          <MomentoModalContent
            width="min(420px, 92vw)"
            height="auto"
            style={{
              minHeight: 260,
              padding: "8px 0 0",
              justifyContent: "flex-start",
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <BackButtonArea>
              <LeftItem>
                <BasicImgWrapper
                  src={ExitIcon}
                  onClick={onCloseModal}
                  style={{ width: "40px", height: "40px", cursor: "pointer" }}
                />
              </LeftItem>
            </BackButtonArea>
            <div style={{ padding: "8px 24px 0", flex: 1 }}>
              <BasicText
                fontFamily={FontFamilyTypeEnum.MONTSERRAT}
                fontSize="20px"
                fontWeight="600"
                margin="0 0 12px"
              >
                {copy.title}
              </BasicText>
              <BasicText
                fontFamily={FontFamilyTypeEnum.MONTSERRAT}
                fontSize="15px"
                lineHeight="22px"
                textAlign="left"
                margin="0"
              >
                {copy.body}
              </BasicText>
            </div>
            {footerRow}
          </MomentoModalContent>
        ) : null}
        {!textOnly && sizesCalculated ? (
          <MomentoModalContent width={canvasAreaSize} height={canvasAreaSize}>
            <BackButtonArea>
              <LeftItem>
                <BasicImgWrapper
                  src={ExitIcon}
                  onClick={onCloseModal}
                  style={{ width: "40px", height: "40px", cursor: "pointer" }}
                />
              </LeftItem>
            </BackButtonArea>
            {popup && onTapImage ? (
              <button
                type="button"
                onClick={onTapImage}
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  cursor: "pointer",
                  maxWidth: "80%",
                  margin: "0 auto",
                }}
                aria-label="Open pop-up action"
              >
                <BasicImgWrapper
                  src={src}
                  width={imageAreaSize}
                  height={imageAreaSize}
                  style={{ maxWidth: "100%", maxHeight: "80%", margin: "0 auto", display: "block" }}
                />
              </button>
            ) : (
              <BasicImgWrapper
                src={src}
                width={imageAreaSize}
                height={imageAreaSize}
                style={{ maxWidth: "80%", maxHeight: "80%", margin: "0 auto" }}
              />
            )}
            {requireExplicitDismiss ? footerRow : null}
          </MomentoModalContent>
        ) : null}
      </MomentoModal>
    </>
  );
};

export default TeamPopupComponent;
